import { motion } from "framer-motion";
import { Shield, Heart, Clock, Target, Zap, ExternalLink, CreditCard, Gift, Star } from "lucide-react";

const DangerZone = () => {
  const tiers = [
    {
      name: "Kitten Crew",
      price: "$5+",
      priceNote: "/month",
      benefits: [
        "Help save animals",
        "1 Sticker per quarter",
      ],
      featured: false,
    },
    {
      name: "Dog Pack",
      price: "$25+",
      priceNote: "/month",
      benefits: [
        "Help save animals",
        "1 Sticker per quarter",
        "1 Merch item per quarter*",
      ],
      featured: true,
    },
    {
      name: "Big Dog",
      price: "$50+",
      priceNote: "/month",
      benefits: [
        "Help save animals",
        "2 Stickers per quarter",
        "2 Merch items per quarter*",
      ],
      featured: false,
    },
  ];

  const benefits = [
    {
      icon: Shield,
      title: "Stable, Predictable Income",
      description: "Monthly donations provide a reliable stream of income, which helps us plan and budget more effectively. Unlike one-time donations, which can be unpredictable, recurring donations allow us to know how much money we can count on each month.",
    },
    {
      icon: Heart,
      title: "Ongoing Animal Care",
      description: "Caring for animals is not a one-time expense. Rescued animals need food, medical care (vaccinations, surgeries, treatments), bedding, training and rehabilitation. These needs are ongoing, and monthly donations help ensure they're consistently met.",
    },
    {
      icon: Zap,
      title: "Emergency Preparedness",
      description: "As a rescue, we often face unexpected emergencies—injured animals, natural disasters, cruelty cases, etc. Having a steady base of monthly donors gives us the flexibility to respond quickly, without having to launch a fundraiser in a crisis.",
    },
    {
      icon: Target,
      title: "Long-term Planning",
      description: "Knowing we have committed monthly support allows us to invest in better facilities, plan outreach and adoption events, expand programs, and save more animals.",
    },
    {
      icon: Clock,
      title: "Saves Time and Resources",
      description: "Rather than constantly running fundraising campaigns, monthly donors allow our rescue team to focus more energy on animal care, advocacy, and education.",
    },
  ];

  return (
    <div data-testid="danger-zone-page">
      {/* Hero Section with Logo */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-[#1A1C18] to-[#0A0A0A] relative overflow-hidden">
        <div className="absolute inset-0 bg-[#FFD700]/5" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {/* Logo */}
            <img 
              src="/danger-zone-logo.png" 
              alt="3 Eye Blind Rescue Club - The Danger Zone" 
              className="mx-auto max-w-md lg:max-w-lg mb-8"
            />
            <p className="text-xl text-[#A3A3A3] max-w-3xl mx-auto mb-4">
              Named after our founder&apos;s favorite Top Gun moment, the Danger Zone is our exclusive monthly donor membership.
            </p>
            <p className="text-lg text-white max-w-2xl mx-auto">
              Join our squadron of dedicated supporters who provide consistent, reliable funding for our rescue operations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Membership Tiers */}
      <section className="py-24 border-y border-[#4A5320]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              Choose Your Level
            </span>
            <h2 className="font-['Bebas_Neue'] text-5xl text-white mb-4">
              Become a Rescue Club Member
            </h2>
            <p className="text-[#A3A3A3] text-lg">
              Sign up for monthly donations and join the mission
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {tiers.map((tier, index) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative p-8 ${
                  tier.featured 
                    ? 'bg-[#4A5320]/30 border-2 border-[#FFD700]' 
                    : 'bg-[#1A1C18] border border-[#4A5320]'
                }`}
              >
                {tier.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-[#FFD700] text-black font-mono text-xs uppercase tracking-widest px-4 py-1">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="font-['Bebas_Neue'] text-3xl text-white mb-2">{tier.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-['Bebas_Neue'] text-5xl text-[#FFD700]">{tier.price}</span>
                    <span className="text-[#A3A3A3] text-sm">{tier.priceNote}</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {tier.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#A3A3A3]">
                      <Star className="w-5 h-5 text-[#FFD700] flex-shrink-0 mt-0.5" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="https://www.zeffy.com/ticketing/three-eye-blind-rescue-club-the-danger-zone"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full inline-flex items-center justify-center gap-2 py-3 px-6 font-bold uppercase tracking-wide transition-all ${
                    tier.featured
                      ? 'bg-[#FFD700] text-black hover:-translate-y-1'
                      : 'bg-transparent border-2 border-[#4A5320] text-white hover:bg-[#4A5320]'
                  }`}
                >
                  Join Now
                  <ExternalLink className="w-4 h-4" />
                </a>
              </motion.div>
            ))}
          </div>

          {/* Merch Note */}
          <p className="text-center text-[#A3A3A3] text-sm">
            *Merch will be an exclusive branded item such as apparel or drinkware
          </p>
        </div>
      </section>

      {/* Join Options */}
      <section className="py-24 bg-[#1A1C18]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              Payment Options
            </span>
            <h2 className="font-['Bebas_Neue'] text-4xl text-white">
              How to Join
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.a
              href="https://www.zeffy.com/ticketing/three-eye-blind-rescue-club-the-danger-zone"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              data-testid="danger-zone-card-button"
              className="group block p-8 bg-[#0A0A0A] border border-[#4A5320] hover:border-[#FFD700] transition-all"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-[#4A5320] flex items-center justify-center mb-6 group-hover:bg-[#FFD700] transition-colors">
                  <CreditCard className="w-8 h-8 text-[#FFD700] group-hover:text-black transition-colors" />
                </div>
                <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-2">Credit/Debit Card</h3>
                <p className="text-[#A3A3A3] text-sm mb-4">
                  Join securely via Zeffy. 100% of your donation goes to the animals.
                </p>
                <span className="inline-flex items-center gap-2 text-[#FFD700] font-mono text-sm uppercase tracking-widest">
                  Join Now
                  <ExternalLink className="w-4 h-4" />
                </span>
              </div>
            </motion.a>

            <motion.a
              href="https://www.paypal.com/donate/?hosted_button_id=NC2K87YS7X5DG"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              data-testid="danger-zone-paypal-button"
              className="group block p-8 bg-[#0A0A0A] border border-[#4A5320] hover:border-[#FFD700] transition-all"
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 bg-[#4A5320] flex items-center justify-center mb-6 group-hover:bg-[#FFD700] transition-colors">
                  <Heart className="w-8 h-8 text-[#FFD700] group-hover:text-black transition-colors" />
                </div>
                <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-2">PayPal</h3>
                <p className="text-[#A3A3A3] text-sm mb-4">
                  Use your PayPal account for recurring monthly donations.
                </p>
                <span className="inline-flex items-center gap-2 text-[#FFD700] font-mono text-sm uppercase tracking-widest">
                  Join with PayPal
                  <ExternalLink className="w-4 h-4" />
                </span>
              </div>
            </motion.a>
          </div>
        </div>
      </section>

      {/* Why Monthly Donations */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              The Impact
            </span>
            <h2 className="font-['Bebas_Neue'] text-5xl lg:text-6xl text-white mb-6">
              Why We Need Monthly Donations
            </h2>
            <p className="text-[#A3A3A3] text-lg max-w-2xl mx-auto">
              Your recurring support is the backbone of our rescue operations. Here&apos;s why monthly giving makes such a difference.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-tactical p-8"
              >
                <div className="w-14 h-14 bg-[#4A5320] flex items-center justify-center mb-6">
                  <benefit.icon className="w-7 h-7 text-[#FFD700]" />
                </div>
                <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-3">{benefit.title}</h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#4A5320]/20 border-y border-[#4A5320]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Bebas_Neue'] text-5xl lg:text-6xl text-white mb-6">
              Ready to Enter the <span className="text-[#FFD700]">Danger Zone</span>?
            </h2>
            <p className="text-[#A3A3A3] text-lg mb-8 max-w-2xl mx-auto">
              Join our squadron of monthly donors and become a vital part of our mission. Your consistent support helps us save more lives, every single month.
            </p>
            <a
              href="https://www.zeffy.com/ticketing/three-eye-blind-rescue-club-the-danger-zone"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="danger-zone-cta-button"
              className="btn-primary inline-flex items-center gap-2 text-lg"
            >
              Join the Danger Zone
              <ExternalLink className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Tax Info */}
      <section className="py-12 border-t border-[#4A5320]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#A3A3A3] text-sm font-mono">
            Three Eye Blind Animal Rescue is a registered 501(c)(3) nonprofit organization.<br />
            All donations are tax-deductible. <span className="text-white">EIN: 99-0978241</span>
          </p>
        </div>
      </section>
    </div>
  );
};

export default DangerZone;
